import { tryGetDirectory, readTextFiles, readFileHandles } from "../../storage/fsa.ts";
import { formatBytesPlain } from "../../modules/shared/index-page.ts";
import type { ModuleIndexData, IndexRow } from "../../modules/shared/module-index.ts";
import type { ModuleId } from "../../auth/config.ts";

/**
 * Rebuilds a module's index straight from the raw files it already keeps on
 * disk, without the export-time `index.json` sidecar and without any network.
 *
 * This is what keeps Browse honest. The sidecar is only written while an
 * export actually runs; a folder from an interrupted run, or from a version
 * before the sidecar existed, would otherwise show a module as "not exported"
 * even though its data is right there. Reading the raw files (the same ones
 * the standalone pages were built from) means the count and rows always
 * reflect what is actually on disk.
 *
 * Columns, itemKind and hrefs mirror each module's export-time index exactly
 * so the viewer treats a rebuilt index identically to a written one.
 */

/** `Personal_a1b2c3d4` -> `Personal` — the label the on-disk key was built from. */
function labelFromKey(key: string): string {
    return key.replace(/_[0-9a-f]{8,16}$/i, "") || key;
}

function toLocale(value: string | null | undefined): string {
    if (!value) {
        return "";
    }
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? value : new Date(ms).toLocaleString();
}

async function subdirNames(dir: FileSystemDirectoryHandle): Promise<string[]> {
    const names: string[] = [];
    for await (const [name, handle] of dir.entries()) {
        if (handle.kind === "directory") {
            names.push(name);
        }
    }
    return names;
}

interface StoredMessageLike {
    createdDateTime?: string;
    html?: string;
}

async function rebuildTeams(root: FileSystemDirectoryHandle): Promise<IndexRow[]> {
    const teamsDir = await tryGetDirectory(root, ["teams"]);
    if (!teamsDir) {
        return [];
    }
    const rows: IndexRow[] = [];

    for (const key of await subdirNames(teamsDir)) {
        const messagesDir = await tryGetDirectory(teamsDir, [key, "messages"]);
        if (!messagesDir) {
            continue;
        }
        let count = 0;
        let images = 0;
        let first: string | undefined;
        let last: string | undefined;
        for await (const { name, text } of readTextFiles(messagesDir)) {
            if (!name.endsWith(".json")) {
                continue;
            }
            let message: StoredMessageLike;
            try {
                message = JSON.parse(text) as StoredMessageLike;
            } catch {
                continue;
            }
            count += 1;
            images += (message.html?.match(/<img[\s>]/gi) ?? []).length;
            const created = message.createdDateTime;
            if (created) {
                if (!first || created < first) first = created;
                if (!last || created > last) last = created;
            }
        }
        if (count === 0) {
            continue;
        }
        rows.push({
            cells: {
                conversation: labelFromKey(key),
                type: "",
                messages: String(count),
                images: String(images),
                first: first ? toLocale(first) : "",
                last: last ? toLocale(last) : ""
            },
            sortValues: { messages: count, images, first: first ?? "", last: last ?? "" },
            href: `${key}/index.html`
        });
    }

    rows.sort((a, b) => (a.cells.conversation ?? "").localeCompare(b.cells.conversation ?? ""));
    return rows;
}

interface MailEntryLike {
    filename: string;
    subject: string | null;
    from: string | null;
    date: string | null;
}

async function collectMailFolders(
    dir: FileSystemDirectoryHandle,
    relative: string[],
    out: { relDir: string; entries: MailEntryLike[] }[]
): Promise<void> {
    let indexHandle: FileSystemFileHandle | null = null;
    const subdirs: [string, FileSystemDirectoryHandle][] = [];
    for await (const [name, handle] of dir.entries()) {
        if (handle.kind === "file" && name === "_index.json") {
            indexHandle = handle;
        } else if (handle.kind === "directory") {
            subdirs.push([name, handle]);
        }
    }
    if (indexHandle) {
        try {
            const entries = JSON.parse(await (await indexHandle.getFile()).text()) as MailEntryLike[];
            out.push({ relDir: relative.join("/"), entries });
        } catch {
            // Skip an unreadable folder index rather than fail the rebuild.
        }
    }
    for (const [name, handle] of subdirs) {
        await collectMailFolders(handle, [...relative, name], out);
    }
}

async function rebuildMail(root: FileSystemDirectoryHandle): Promise<IndexRow[]> {
    const mailDir = await tryGetDirectory(root, ["mail"]);
    if (!mailDir) {
        return [];
    }
    const folders: { relDir: string; entries: MailEntryLike[] }[] = [];
    await collectMailFolders(mailDir, [], folders);

    const rows: IndexRow[] = [];
    for (const { relDir, entries } of folders) {
        // relDir is "<mailbox>/<folder path>"; the folder label drops the mailbox.
        const parts = relDir.split("/");
        const folderLabel = parts.slice(1).join("/") || parts[0] || "";
        for (const e of entries) {
            rows.push({
                cells: {
                    subject: e.subject || "(no subject)",
                    from: e.from ?? "",
                    date: toLocale(e.date),
                    folder: folderLabel
                },
                sortValues: { date: e.date ? Date.parse(e.date) || 0 : 0 },
                href: `${relDir}/${e.filename}`
            });
        }
    }
    return rows;
}

interface CalendarEventLike {
    subject?: string;
    start?: { dateTime?: string };
    end?: { dateTime?: string };
    location?: { displayName?: string };
    isCancelled?: boolean;
}

async function rebuildCalendar(root: FileSystemDirectoryHandle): Promise<IndexRow[]> {
    const rawDir = await tryGetDirectory(root, ["calendar", "_raw"]);
    if (!rawDir) {
        return [];
    }
    const rows: IndexRow[] = [];
    for (const key of await subdirNames(rawDir)) {
        const calDir = await tryGetDirectory(rawDir, [key]);
        if (!calDir) {
            continue;
        }
        const label = labelFromKey(key);
        for await (const { name, text } of readTextFiles(calDir)) {
            if (!name.endsWith(".json")) {
                continue;
            }
            let event: CalendarEventLike;
            try {
                event = JSON.parse(text) as CalendarEventLike;
            } catch {
                continue;
            }
            if (event.isCancelled) {
                continue;
            }
            rows.push({
                cells: {
                    subject: event.subject || "(no subject)",
                    start: toLocale(event.start?.dateTime),
                    end: toLocale(event.end?.dateTime),
                    calendar: label,
                    location: event.location?.displayName ?? ""
                },
                sortValues: { start: Date.parse(event.start?.dateTime ?? "") || 0 },
                href: `${key}.ics`
            });
        }
    }
    return rows;
}

interface ContactLike {
    displayName?: string;
    emailAddresses?: { address?: string }[];
    mobilePhone?: string;
    businessPhones?: string[];
    homePhones?: string[];
    companyName?: string;
}

async function rebuildContacts(root: FileSystemDirectoryHandle): Promise<IndexRow[]> {
    const rawDir = await tryGetDirectory(root, ["contacts", "_raw"]);
    if (!rawDir) {
        return [];
    }
    const contacts: ContactLike[] = [];
    for await (const { name, text } of readTextFiles(rawDir)) {
        if (!name.endsWith(".json")) {
            continue;
        }
        try {
            contacts.push(JSON.parse(text) as ContactLike);
        } catch {
            // skip corrupt
        }
    }
    contacts.sort((a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? ""));
    return contacts.map((c) => ({
        cells: {
            name: c.displayName || "(no name)",
            email: c.emailAddresses?.[0]?.address ?? "",
            phone: c.mobilePhone || c.businessPhones?.[0] || c.homePhones?.[0] || "",
            company: c.companyName ?? ""
        }
    }));
}

interface RenderedTaskLike {
    title?: string;
    status?: string;
    due?: string | null;
}

async function rebuildTasks(root: FileSystemDirectoryHandle): Promise<IndexRow[]> {
    const tasksDir = await tryGetDirectory(root, ["tasks"]);
    if (!tasksDir) {
        return [];
    }
    const rows: IndexRow[] = [];
    for await (const { name, handle } of readFileHandles(tasksDir)) {
        if (!name.endsWith(".json") || name === "index.json") {
            continue;
        }
        const key = name.replace(/\.json$/, "");
        const label = labelFromKey(key);
        let tasks: RenderedTaskLike[];
        try {
            tasks = JSON.parse(await (await handle.getFile()).text()) as RenderedTaskLike[];
        } catch {
            continue;
        }
        for (const task of tasks) {
            rows.push({
                cells: {
                    title: task.title || "(no title)",
                    list: label,
                    status: task.status ?? "",
                    due: task.due ? new Date(task.due).toLocaleDateString() : ""
                },
                sortValues: { due: task.due ? Date.parse(task.due) || 0 : 0 },
                href: `${key}.json`
            });
        }
    }
    return rows;
}

async function rebuildOneNote(root: FileSystemDirectoryHandle): Promise<IndexRow[]> {
    const onenoteDir = await tryGetDirectory(root, ["onenote"]);
    if (!onenoteDir) {
        return [];
    }
    const rows: IndexRow[] = [];
    for (const nbKey of await subdirNames(onenoteDir)) {
        const nbDir = await tryGetDirectory(onenoteDir, [nbKey]);
        if (!nbDir) {
            continue;
        }
        for (const secKey of await subdirNames(nbDir)) {
            const secDir = await tryGetDirectory(nbDir, [secKey]);
            if (!secDir) {
                continue;
            }
            for await (const { name, text } of readTextFiles(secDir)) {
                if (!name.endsWith(".html")) {
                    continue;
                }
                const titleMatch = text.match(/<title>([\s\S]*?)<\/title>/i);
                rows.push({
                    cells: {
                        title: (titleMatch?.[1] ?? name.replace(/\.html$/, "")) || "(untitled page)",
                        notebook: labelFromKey(nbKey),
                        section: labelFromKey(secKey)
                    },
                    href: `${nbKey}/${secKey}/${name}`
                });
            }
        }
    }
    rows.sort(
        (a, b) =>
            (a.cells.notebook ?? "").localeCompare(b.cells.notebook ?? "") ||
            (a.cells.section ?? "").localeCompare(b.cells.section ?? "")
    );
    return rows;
}

async function rebuildOneDrive(root: FileSystemDirectoryHandle): Promise<IndexRow[]> {
    const dir = await tryGetDirectory(root, ["onedrive"]);
    if (!dir) {
        return [];
    }
    const rows: IndexRow[] = [];
    const walk = async (d: FileSystemDirectoryHandle, prefix: string): Promise<void> => {
        for await (const [name, handle] of d.entries()) {
            const rel = prefix ? `${prefix}/${name}` : name;
            if (handle.kind === "directory") {
                await walk(handle, rel);
            } else if (rel !== "index.html" && rel !== "index.json") {
                const file = await handle.getFile();
                rows.push({
                    cells: { name, path: rel, size: formatBytesPlain(file.size) },
                    sortValues: { size: file.size },
                    href: rel
                });
            }
        }
    };
    await walk(dir, "");
    rows.sort((a, b) => (a.cells.path ?? "").localeCompare(b.cells.path ?? ""));
    return rows;
}

const COLUMNS: Record<ModuleId, ModuleIndexData["columns"]> = {
    teams: [
        { key: "conversation", label: "Conversation" },
        { key: "type", label: "Type" },
        { key: "messages", label: "Messages", numeric: true },
        { key: "images", label: "Images", numeric: true },
        { key: "first", label: "First activity" },
        { key: "last", label: "Last activity" }
    ],
    mail: [
        { key: "subject", label: "Subject" },
        { key: "from", label: "From" },
        { key: "date", label: "Date" },
        { key: "folder", label: "Folder" }
    ],
    onedrive: [
        { key: "name", label: "File" },
        { key: "path", label: "Path" },
        { key: "size", label: "Size", numeric: true }
    ],
    calendar: [
        { key: "subject", label: "Subject" },
        { key: "start", label: "Start" },
        { key: "end", label: "End" },
        { key: "calendar", label: "Calendar" },
        { key: "location", label: "Location" }
    ],
    contacts: [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "company", label: "Company" }
    ],
    tasks: [
        { key: "title", label: "Title" },
        { key: "list", label: "List" },
        { key: "status", label: "Status" },
        { key: "due", label: "Due" }
    ],
    onenote: [
        { key: "title", label: "Page" },
        { key: "notebook", label: "Notebook" },
        { key: "section", label: "Section" }
    ]
};

const ITEM_KIND: Record<ModuleId, ModuleIndexData["itemKind"]> = {
    teams: "html",
    mail: "file",
    onedrive: "file",
    calendar: "data",
    contacts: "data",
    tasks: "data",
    onenote: "html"
};

const REBUILDERS: Record<ModuleId, (root: FileSystemDirectoryHandle) => Promise<IndexRow[]>> = {
    teams: rebuildTeams,
    mail: rebuildMail,
    onedrive: rebuildOneDrive,
    calendar: rebuildCalendar,
    contacts: rebuildContacts,
    tasks: rebuildTasks,
    onenote: rebuildOneNote
};

/**
 * Rebuilds a module's index from disk, or returns null when there is genuinely
 * nothing on disk for it.
 */
export async function rebuildModuleIndex(
    root: FileSystemDirectoryHandle,
    moduleId: ModuleId
): Promise<ModuleIndexData | null> {
    const rows = await REBUILDERS[moduleId](root);
    if (rows.length === 0) {
        return null;
    }
    return {
        moduleId,
        title: moduleId,
        subtitle: "",
        itemKind: ITEM_KIND[moduleId],
        columns: COLUMNS[moduleId],
        rows,
        generatedAt: new Date().toISOString()
    };
}
